import { isVerboseLoggingEnabled } from '../debug/logging';

const CHANNEL_NAME = 'spotify-player';
const STORAGE_FALLBACK_KEY = '__cmf.spotify-player.message';
const TAB_ID_STORAGE_KEY = '__cmf.spotify.tab-id';
const LEADER_TAB_ID_KEY = '__cmf.spotify.leader-tab-id';
const DEVICE_ID_KEY = '__cmf.spotify.device-id';
const CONTROL_TIMEOUT_MS = 2000;
const LEADER_STALE_MS = 7000;
const WHO_IS_LEADER_GRACE_MS = 1000;

const isDevelopmentEnv =
  typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;
const isDevelopment = isDevelopmentEnv && isVerboseLoggingEnabled();

type Transport = {
  publish: (payload: unknown) => void;
  close: () => void;
};

type Role = 'unknown' | 'leader' | 'follower';

export type ControlPayload =
  | { kind: 'queue-action'; action: string; extras?: Record<string, unknown> }
  | { kind: 'playback'; action: string; payload?: Record<string, unknown> };

export type PlayerStateBroadcast = {
  trackUri: string | null;
  positionMs: number;
  durationMs: number | null;
  paused: boolean;
  deviceId: string | null;
  volume?: number | null;
  shuffle?: boolean | null;
  repeatMode?: 'off' | 'context' | 'track' | null;
  queueSessionId?: string | null;
  currentItemId?: string | null;
  currentItemIndex?: number | null;
  revision?: number | null;
  albumName?: string | null;
  artists?: Array<{ name: string; id?: string | null }>;
  trackName?: string | null;
  sentAt?: number;
  seq?: number;
  leaderSessionId?: string;
};

type WhoIsLeaderMessage = {
  type: 'WHO_IS_LEADER';
  tabId: string;
  sentAt: number;
};

type IAmLeaderMessage = {
  type: 'I_AM_LEADER';
  tabId: string;
  leaderSessionId: string;
  sentAt: number;
};

type DeviceReadyMessage = {
  type: 'SDK_DEVICE_READY';
  tabId: string;
  leaderSessionId: string;
  deviceId: string;
  sentAt: number;
};

type PlayerStateMessage = {
  type: 'PLAYER_STATE';
  tabId: string;
  leaderSessionId: string;
  seq: number;
  sentAt: number;
  payload: PlayerStateBroadcast;
};

export type ControlMessage = {
  type: 'CONTROL';
  tabId: string;
  requestId: string;
  sentAt: number;
  action: ControlPayload;
};

type ControlAckMessage = {
  type: 'CONTROL_ACK';
  tabId: string;
  leaderSessionId: string | null;
  requestId: string;
  ok: boolean;
  error?: string;
  sentAt: number;
};

type LeaderGoingAwayMessage = {
  type: 'LEADER_GOING_AWAY';
  tabId: string;
  leaderSessionId: string | null;
  sentAt: number;
};

type TransportMessage =
  | WhoIsLeaderMessage
  | IAmLeaderMessage
  | DeviceReadyMessage
  | PlayerStateMessage
  | ControlMessage
  | ControlAckMessage
  | LeaderGoingAwayMessage;

type CoordinatorCallbacks = {
  onRoleChange?: (role: Role, info: { leaderTabId: string | null; leaderSessionId: string | null }) => void;
  onControl?: (message: ControlMessage) => Promise<{ ok: boolean; error?: string } | void> | { ok: boolean; error?: string } | void;
  onDeviceReady?: (deviceId: string, leaderSessionId: string | null) => void;
  onPlayerState?: (state: PlayerStateBroadcast) => void;
  onLeaderHint?: () => void;
};

const noopTransport: Transport = {
  publish: () => {},
  close: () => {},
};

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const readSessionStorage = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeSessionStorage = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage?.setItem(key, value);
  } catch {
    // ignore
  }
};

const readLocalStorage = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const writeLocalStorage = (key: string, value: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage?.removeItem(key);
    } else {
      window.localStorage?.setItem(key, value);
    }
  } catch {
    // ignore
  }
};

const createTransport = (onMessage: (payload: TransportMessage) => void): Transport => {
  if (typeof window === 'undefined') {
    return noopTransport;
  }

  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;
      onMessage(data as TransportMessage);
    };
    return {
      publish: (payload) => channel.postMessage(payload),
      close: () => channel.close(),
    };
  }

  const publishViaStorage = (payload: unknown) => {
    try {
      const envelope = { __id: generateId(), payload };
      window.localStorage?.setItem(STORAGE_FALLBACK_KEY, JSON.stringify(envelope));
      // Clear immediately to reduce replay risk
      window.localStorage?.removeItem(STORAGE_FALLBACK_KEY);
    } catch {
      // ignore
    }
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_FALLBACK_KEY) return;
    if (!event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue) as { payload?: unknown };
      if (parsed && typeof parsed === 'object' && parsed.payload) {
        onMessage(parsed.payload as TransportMessage);
      }
    } catch {
      // ignore malformed payloads
    }
  };

  window.addEventListener('storage', handleStorage);
  return {
    publish: publishViaStorage,
    close: () => window.removeEventListener('storage', handleStorage),
  };
};

export class MultiTabCoordinator {
  private readonly tabId: string;
  private leaderTabId: string | null;
  private leaderSessionId: string | null;
  private role: Role = 'unknown';
  private readonly transport: Transport;
  private readonly callbacks: CoordinatorCallbacks;
  private controlWaiters = new Map<
    string,
    { resolve: (value: { ok: boolean; timeout?: boolean; error?: string }) => void }
  >();
  private electionTimer: number | null = null;
  private playerSeq = 0;
  private lastLeaderAnnouncementAt = 0;
  private electionStartTime: number | null = null;

  constructor(callbacks: CoordinatorCallbacks = {}) {
    const existingTabId = readSessionStorage(TAB_ID_STORAGE_KEY);
    this.tabId = existingTabId ?? generateId();
    if (!existingTabId) {
      writeSessionStorage(TAB_ID_STORAGE_KEY, this.tabId);
    }
    this.leaderTabId = readLocalStorage(LEADER_TAB_ID_KEY);
    this.leaderSessionId = null;
    this.transport = createTransport((payload) => this.handleMessage(payload));
    this.callbacks = callbacks;
  }

  start(): void {
    this.electionStartTime = Date.now();
    console.info('[DeviceInitTiming] Election start', {
      timestamp: new Date().toISOString(),
      tabId: this.tabId,
      leaderTabId: this.leaderTabId,
    });
    this.sendWhoIsLeader();
    // Fast-track election if this is the first tab ever (no leader in localStorage)
    // or if we're restoring as the previous leader (leaderTabId matches our tabId)
    // Otherwise, use normal election timing to respect leader refresh scenarios
    const isFirstTab = !this.leaderTabId;
    const isRestoringLeader = this.leaderTabId === this.tabId;
    const fastTrackDelay = isFirstTab || isRestoringLeader ? 0 : 350;
    console.info('[DeviceInitTiming] Election scheduled', {
      timestamp: new Date().toISOString(),
      delayMs: fastTrackDelay,
      isFirstTab,
      isRestoringLeader,
    });
    this.scheduleElection(fastTrackDelay);
  }

  destroy(): void {
    if (this.electionTimer != null && typeof window !== 'undefined') {
      window.clearTimeout(this.electionTimer);
    }
    this.transport.close();
    this.controlWaiters.forEach(({ resolve }) => resolve({ ok: false, timeout: true }));
    this.controlWaiters.clear();
  }

  isLeader(): boolean {
    return this.role === 'leader';
  }

  getLeaderSessionId(): string | null {
    return this.leaderSessionId;
  }

  getLeaderTabId(): string | null {
    return this.leaderTabId;
  }

  getTabId(): string {
    return this.tabId;
  }

  heartbeat(): void {
    if (this.role !== 'leader') return;
    this.broadcast({
      type: 'I_AM_LEADER',
      tabId: this.tabId,
      leaderSessionId: this.leaderSessionId ?? generateId(),
      sentAt: Date.now(),
    });
  }

  promoteToLeader(reason: string = 'self-promote'): void {
    if (this.role === 'leader') return;
    const startTime = this.electionStartTime ?? Date.now();
    const duration = Date.now() - startTime;
    this.role = 'leader';
    this.leaderTabId = this.tabId;
    this.leaderSessionId = generateId();
    this.playerSeq = 0;
    writeLocalStorage(LEADER_TAB_ID_KEY, this.tabId);
    console.info('[DeviceInitTiming] Election complete - promoted to leader', {
      timestamp: new Date().toISOString(),
      durationMs: duration,
      reason,
      tabId: this.tabId,
    });
    this.callbacks.onRoleChange?.('leader', {
      leaderTabId: this.leaderTabId,
      leaderSessionId: this.leaderSessionId,
    });
    this.broadcast({
      type: 'I_AM_LEADER',
      tabId: this.tabId,
      leaderSessionId: this.leaderSessionId,
      sentAt: Date.now(),
    });
    if (isDevelopment) {
      console.debug('[MultiTab] Promoted to leader', { reason, tabId: this.tabId });
    }
  }

  broadcastDeviceReady(deviceId: string): void {
    if (!this.isLeader() || !this.leaderSessionId) return;
    writeLocalStorage(DEVICE_ID_KEY, deviceId);
    this.broadcast({
      type: 'SDK_DEVICE_READY',
      tabId: this.tabId,
      leaderSessionId: this.leaderSessionId,
      deviceId,
      sentAt: Date.now(),
    });
  }

  broadcastPlayerState(payload: PlayerStateBroadcast): void {
    if (!this.isLeader() || !this.leaderSessionId) return;
    const seq = ++this.playerSeq;
    this.broadcast({
      type: 'PLAYER_STATE',
      tabId: this.tabId,
      leaderSessionId: this.leaderSessionId,
      seq,
      sentAt: Date.now(),
      payload: {
        ...payload,
        seq,
        leaderSessionId: this.leaderSessionId,
        sentAt: Date.now(),
      },
    });
  }

  async sendControl(
    action: ControlPayload
  ): Promise<{ ok: boolean; timeout?: boolean; error?: string }> {
    if (!this.leaderTabId || this.role === 'leader') {
      return { ok: false, timeout: true };
    }
    const requestId = generateId();
    const sentAt = Date.now();
    const payload: ControlMessage = {
      type: 'CONTROL',
      tabId: this.tabId,
      requestId,
      sentAt,
      action,
    };
    const result = new Promise<{ ok: boolean; timeout?: boolean; error?: string }>((resolve) => {
      this.controlWaiters.set(requestId, { resolve });
    });
    this.broadcast(payload);
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        const waiter = this.controlWaiters.get(requestId);
        if (waiter) {
          this.controlWaiters.delete(requestId);
          waiter.resolve({ ok: false, timeout: true });
        }
      }, CONTROL_TIMEOUT_MS);
    }
    return result;
  }

  leaderGoingAway(): void {
    if (!this.isLeader()) return;
    this.broadcast({
      type: 'LEADER_GOING_AWAY',
      tabId: this.tabId,
      leaderSessionId: this.leaderSessionId,
      sentAt: Date.now(),
    });
  }

  private broadcast(message: TransportMessage): void {
    this.transport.publish(message);
  }

  private sendWhoIsLeader(): void {
    this.broadcast({
      type: 'WHO_IS_LEADER',
      tabId: this.tabId,
      sentAt: Date.now(),
    });
  }

  private scheduleElection(delayMs: number = 350): void {
    if (typeof window === 'undefined') return;
    if (this.electionTimer != null) {
      window.clearTimeout(this.electionTimer);
    }
    this.electionTimer = window.setTimeout(() => {
      if (this.role === 'leader') return;
      const now = Date.now();
      const heardLeaderRecently =
        this.lastLeaderAnnouncementAt > 0 && now - this.lastLeaderAnnouncementAt < LEADER_STALE_MS;
      if (this.leaderTabId && this.leaderTabId === this.tabId) {
        this.promoteToLeader('restore-leader');
        return;
      }

      if (!heardLeaderRecently) {
        // Probe for a leader and wait a short grace before promoting.
        this.sendWhoIsLeader();
        if (this.electionTimer != null) {
          window.clearTimeout(this.electionTimer);
        }
        this.electionTimer = window.setTimeout(() => {
          if (this.role === 'leader') return;
          const nowProbe = Date.now();
          const heardDuringProbe =
            this.lastLeaderAnnouncementAt > 0 &&
            nowProbe - this.lastLeaderAnnouncementAt < LEADER_STALE_MS;
          if (!heardDuringProbe) {
            this.promoteToLeader('no-leader-response');
          } else {
            this.scheduleElection(LEADER_STALE_MS);
          }
        }, WHO_IS_LEADER_GRACE_MS);
        return;
      }

      // Leader is talking; check again after the stale window.
      this.scheduleElection(LEADER_STALE_MS);
    }, delayMs);
  }

  private handleMessage(message: TransportMessage): void {
    switch (message.type) {
      case 'WHO_IS_LEADER':
        if (this.role === 'leader') {
          this.broadcast({
            type: 'I_AM_LEADER',
            tabId: this.tabId,
            leaderSessionId: this.leaderSessionId ?? generateId(),
            sentAt: Date.now(),
          });
        }
        break;
      case 'I_AM_LEADER':
        this.handleLeaderAnnouncement(message);
        break;
      case 'SDK_DEVICE_READY':
        this.handleDeviceReady(message);
        break;
      case 'PLAYER_STATE':
        this.handlePlayerState(message);
        break;
      case 'CONTROL':
        void this.handleControl(message);
        break;
      case 'CONTROL_ACK':
        this.handleControlAck(message);
        break;
      case 'LEADER_GOING_AWAY':
        this.handleLeaderGoingAway(message);
        break;
      default:
        break;
    }
  }

  private handleLeaderAnnouncement(message: IAmLeaderMessage): void {
    if (this.role === 'leader') {
      return;
    }
    console.debug('[MultiTab] leader-announcement', {
      tabId: this.tabId,
      currentLeader: this.leaderTabId,
      announcedLeader: message.tabId,
      leaderSessionId: message.leaderSessionId,
      sentAt: message.sentAt
    });
    const sentAt = message.sentAt ?? 0;
    if (sentAt < this.lastLeaderAnnouncementAt) {
      return;
    }
    const preferredLeader = this.leaderTabId && this.leaderTabId === message.tabId;
    if (!this.leaderTabId || preferredLeader) {
      this.leaderTabId = message.tabId;
      writeLocalStorage(LEADER_TAB_ID_KEY, this.leaderTabId);
    }
    this.leaderSessionId = message.leaderSessionId;
    this.lastLeaderAnnouncementAt = sentAt;
    if (this.role !== 'follower' || preferredLeader) {
      this.role = 'follower';
      this.playerSeq = 0;
      this.callbacks.onRoleChange?.('follower', {
        leaderTabId: this.leaderTabId,
        leaderSessionId: this.leaderSessionId,
      });
    }
  }

  private handleDeviceReady(message: DeviceReadyMessage): void {
    if (!this.leaderTabId || message.tabId !== this.leaderTabId) {
      return;
    }
    writeLocalStorage(DEVICE_ID_KEY, message.deviceId);
    this.callbacks.onDeviceReady?.(message.deviceId, message.leaderSessionId);
  }

  private handlePlayerState(message: PlayerStateMessage): void {
    if (!this.leaderTabId || message.tabId !== this.leaderTabId) return;
    if (this.leaderSessionId && message.leaderSessionId !== this.leaderSessionId) {
      this.playerSeq = 0;
    }
    this.leaderSessionId = message.leaderSessionId;
    if (message.seq <= this.playerSeq) {
      return;
    }
    this.playerSeq = message.seq;
    this.callbacks.onPlayerState?.(message.payload);
  }

  private async handleControl(message: ControlMessage): Promise<void> {
    if (!this.isLeader()) return;
    console.debug('[MultiTab] control:received', {
      action: message.action?.kind,
      playbackAction: (message.action as { action?: string })?.action,
      payloadKeys:
        message.action && typeof message.action === 'object' && 'payload' in message.action
          ? Object.keys((message.action as { payload?: Record<string, unknown> }).payload ?? {})
          : [],
      requestId: message.requestId
    });
    const ack = (result: { ok: boolean; error?: string }) => {
      this.broadcast({
        type: 'CONTROL_ACK',
        tabId: this.tabId,
        leaderSessionId: this.leaderSessionId,
        requestId: message.requestId,
        ok: result.ok,
        error: result.error,
        sentAt: Date.now(),
      });
    };

    // Send immediate receipt ack to avoid follower timeouts.
    ack({ ok: true });

    try {
      const result = (await this.callbacks.onControl?.(message)) ?? { ok: true };
      if (!result.ok) {
        ack(result);
      }
    } catch (error) {
      ack({
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  private handleControlAck(message: ControlAckMessage): void {
    const waiter = this.controlWaiters.get(message.requestId);
    if (!waiter) return;
    this.controlWaiters.delete(message.requestId);
    waiter.resolve({ ok: message.ok, error: message.error });
  }

  private handleLeaderGoingAway(message: LeaderGoingAwayMessage): void {
    if (this.leaderTabId && message.tabId === this.leaderTabId) {
      this.leaderTabId = null;
      this.leaderSessionId = null;
      this.playerSeq = 0;
      this.lastLeaderAnnouncementAt = 0;
      this.callbacks.onLeaderHint?.();
      this.callbacks.onRoleChange?.('unknown', {
        leaderTabId: null,
        leaderSessionId: null,
      });
      this.sendWhoIsLeader();
      this.scheduleElection();
    }
  }
}
